import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FaRupeeSign, 
  FaChartLine, 
  FaMapMarkerAlt,
  FaCogs,
  FaStar,
  FaTrophy
} from 'react-icons/fa';
import { getBranchStats } from '../store/statsSlice';

const BranchData = ({ userRole = 'Admin' }) => {
  const dispatch = useDispatch();
  const { branchStats, branchLoading } = useSelector((state) => state.stats);

  useEffect(() => {
    // Fetch actual branch statistics from API
    dispatch(getBranchStats());
  }, [dispatch, userRole]);

  const BranchCard = ({ title, salesValue, profitValue, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 hover:shadow-xl transition-shadow duration-300" 
         style={{ borderLeftColor: color }}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-gray-600 text-sm font-medium uppercase tracking-wider">{title}</p>
          {branchLoading ? (
            <div className="space-y-2 mt-2">
              <div className="animate-pulse bg-gray-200 h-5 w-20 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-4 w-16 rounded"></div>
            </div>
          ) : (
            <div className="mt-2">
              <div className="flex items-center space-x-3">
                <div>
                  <p className="text-lg font-bold text-gray-800">{salesValue}</p>
                  <p className="text-xs text-gray-500">Sales</p>
                </div>
                <div className="text-gray-300">|</div>
                <div>
                  <p className="text-md font-bold text-green-600">{profitValue}</p>
                  <p className="text-xs text-gray-500">Profit</p>
                </div>
              </div>
            </div>
          )}
          {subtitle && (
            <p className="text-gray-500 text-xs mt-2">{subtitle}</p>
          )}
        </div>
        <div className="p-3 rounded-full" style={{ backgroundColor: `${color}20` }}>
          <Icon className="text-xl" style={{ color }} />
        </div>
      </div>
    </div>
  );

  const TopServicesCard = ({ branchName, services, color }) => (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
            <FaMapMarkerAlt className="text-lg" style={{ color }} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">{branchName} Branch</h3>
            <p className="text-gray-600 text-sm">Top Selling Services</p>
          </div>
        </div>
        <FaTrophy className="text-yellow-500 text-2xl" />
      </div>
      
      <div className="space-y-4">
        {branchLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        ) : services.length > 0 ? (
          services.map((service, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" 
                     style={{ backgroundColor: color }}>
                  {index + 1}
                </div>
                <div>
                  <p className="font-medium text-gray-800">{service.name}</p>
                  <p className="text-sm text-gray-600">{service.sales} services completed</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-800">₹{service.revenue.toLocaleString()}</p>
                <div className="flex items-center text-yellow-500">
                  <FaStar className="text-xs mr-1" />
                  <span className="text-xs">Top Service</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <FaCogs className="text-4xl text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No services data available</p>
            <p className="text-sm text-gray-400 mt-1">Add some sales to see top services</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Branch Sales & Profit Data */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaMapMarkerAlt className="mr-3 text-blue-600" />
          Branch Performance
        </h2>
        
        {/* Katra Branch */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <div className="w-3 h-3 bg-blue-600 rounded-full mr-2"></div>
            Katra Branch
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BranchCard
              title="Today's Performance"
              salesValue={`₹${branchStats.katra.todaySales.toLocaleString()}`}
              profitValue={`₹${branchStats.katra.todayProfit.toLocaleString()}`}
              icon={FaRupeeSign}
              color="#3B82F6"
              subtitle="Katra branch daily performance"
            />
            <BranchCard
              title="Monthly Performance"
              salesValue={`₹${branchStats.katra.thisMonthSales.toLocaleString()}`}
              profitValue={`₹${branchStats.katra.thisMonthProfit.toLocaleString()}`}
              icon={FaChartLine}
              color="#10B981"
              subtitle="30% profit margin this month"
            />
          </div>
        </div>

        {/* Meeratola Branch */}
        <div>
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
            <div className="w-3 h-3 bg-green-600 rounded-full mr-2"></div>
            Meeratola Branch
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BranchCard
              title="Today's Performance"
              salesValue={`₹${branchStats.meeratola.todaySales.toLocaleString()}`}
              profitValue={`₹${branchStats.meeratola.todayProfit.toLocaleString()}`}
              icon={FaRupeeSign}
              color="#059669"
              subtitle="Meeratola branch daily performance"
            />
            <BranchCard
              title="Monthly Performance"
              salesValue={`₹${branchStats.meeratola.thisMonthSales.toLocaleString()}`}
              profitValue={`₹${branchStats.meeratola.thisMonthProfit.toLocaleString()}`}
              icon={FaChartLine}
              color="#10B981"
              subtitle="30% profit margin this month"
            />
          </div>
        </div>
      </div>

      {/* Top Selling Services */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaTrophy className="mr-3 text-yellow-500" />
          Top Selling Services
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <TopServicesCard
            branchName="Katra"
            services={branchStats.katra.topServices}
            color="#3B82F6"
          />
          <TopServicesCard
            branchName="Meeratola"
            services={branchStats.meeratola.topServices}
            color="#059669"
          />
        </div>
      </div>
    </div>
  );
};

export default BranchData;
